import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Sparkles,
  Shield,
  CreditCard,
  ArrowRight,
  Loader2,
  TrendingUp,
} from "lucide-react";
import PhoneAnchor from "./PhoneAnchor";
import { SERVICE_GRID } from "./StageServices";

const KEY_TO_DEMO_ID = {
  private_chef: "private-chef",
  massage: "massage",
  airport_ride: "airport-pickup",
  arrival_event: "arrival-event",
  wellness_therapy: "wellness-therapy",
  restaurant_reservations: "restaurant-reservations",
  other: "other",
};

export default function StagePricing({
  previewToggles,
  onToggle,
  pricing,
  onSubscribe,
  actionLoading,
  hostLogo,
  compactHeight = false,
}) {
  const enabledCount = Object.values(previewToggles || {}).filter(Boolean).length;
  const propertyCount = Math.max(Number(pricing?.propertyCount) || 0, 0);

  const PER_PROPERTY_USD = 8;
  const flatMonthlyTotal = propertyCount * PER_PROPERTY_USD;
  const flatPriceLabel = `$${flatMonthlyTotal.toLocaleString("en-US")}`;
  const isQuote = !!pricing?.isQuote;
  const displayedPrice = isQuote ? pricing?.displayPrice : flatPriceLabel;
  const displayedHelper = isQuote
    ? pricing?.helperText
    : `Flat $${PER_PROPERTY_USD} per property per month — no tiers. Includes Bellhopt Concierge guest app + unlimited request handling.`;

  const enabledServices = useMemo(() => {
    const set = new Set(["grocery-delivery"]);
    Object.entries(previewToggles || {}).forEach(([k, on]) => {
      if (on) {
        const id = KEY_TO_DEMO_ID[k];
        if (id) set.add(id);
      }
    });
    return set;
  }, [previewToggles]);

  return (
    <div
      className={`relative w-full laptop:h-full grid laptop:grid-rows-1 laptop:grid-cols-[1.3fr_0.7fr] grid-rows-[auto_auto]`}
    >
      {/* Left — calculator */}
      <div
        className={`relative px-6 laptop:px-14 order-2 laptop:order-1 flex flex-col laptop:min-h-0 laptop:overflow-y-auto ${
          compactHeight ? "py-3 laptop:py-3 laptop:overflow-y-auto" : "py-5 laptop:py-6 laptop:overflow-hidden"
        }`}
      >
        <div className={compactHeight ? "mb-2 laptop:mb-3" : "mb-3 laptop:mb-5"}>
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-1.5">
            Step 3 · Pricing
          </p>
          <h2 className="text-[22px] laptop:text-[28px] font-extrabold text-[#111111] leading-tight" style={{ letterSpacing: -0.4 }}>
            Pricing based on your property count.
          </h2>
        </div>

        {/* Chips row — compact */}
        <div className="laptop:flex-1 laptop:min-h-0 laptop:overflow-y-auto laptop:pr-1">
          <div className="grid grid-cols-2 tabletOnly:grid-cols-3 laptop:grid-cols-3 desktop:grid-cols-4 gap-2">
            {SERVICE_GRID.map((svc) => {
              const on = !!previewToggles?.[svc.key];
              const Icon = svc.icon;
              return (
                <motion.button
                  key={svc.key}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onToggle(svc.key, !on)}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-[10px] border transition-all text-left ${
                    on
                      ? "bg-white border-[#FECACA] shadow-[0_6px_20px_rgba(0,0,0,0.04)]"
                      : "bg-[#F7F7F8] border-[#EAEAEA] hover:bg-white"
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-[8px] flex-shrink-0 flex items-center justify-center ${
                      on ? "bg-[#EF4949] text-white" : "bg-white border border-[#EAEAEA] text-[#888888]"
                    }`}
                  >
                    <Icon size={13} strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block text-[12px] font-bold text-[#111111] truncate">{svc.name}</span>
                    <span className="block text-[10px] font-semibold text-[#10B981]">
                      {on ? "Selected" : "Tap to include"}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Calculator strip */}
        <div
          className={`rounded-[16px] bg-white border border-[#EAEAEA] shadow-[0_18px_60px_rgba(0,0,0,0.05)] ${
            compactHeight ? "mt-3 laptop:mt-4 p-3.5 laptop:p-4" : "mt-4 laptop:mt-6 p-4 laptop:p-5"
          }`}
        >
          <div className="flex items-center justify-between gap-4 flex-wrap laptop:flex-nowrap">
            <div className="flex items-center gap-4 laptop:gap-6 flex-1 min-w-0">
              <Calc label="Services" value={enabledCount} />
              <Divider />
              <Calc label="Properties" value={propertyCount} icon={<Home size={11} />} />
              <Divider />
              <Calc label="Billing" value={isQuote ? "Custom" : "Monthly"} />
            </div>
            <div className="flex items-end gap-1 flex-shrink-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={displayedPrice}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  {isQuote ? (
                    <span
                      className="text-[24px] laptop:text-[30px] font-extrabold text-[#111111] leading-none"
                      style={{ letterSpacing: -0.6 }}
                    >
                      {displayedPrice}
                    </span>
                  ) : (
                    <span
                      className="text-[32px] laptop:text-[40px] font-extrabold text-[#111111] leading-none tabular-nums"
                      style={{ letterSpacing: -0.8 }}
                    >
                      {displayedPrice}
                    </span>
                  )}
                </motion.div>
              </AnimatePresence>
              {!isQuote && (
                <span className="text-[12px] font-semibold text-[#888888] pb-1">/mo</span>
              )}
            </div>
          </div>

          {!isQuote && propertyCount > 0 ? (
            <p className="mt-2 text-[11px] font-semibold text-[#5F6368] tabular-nums">
              {propertyCount} propert{propertyCount === 1 ? "y" : "ies"} × ${PER_PROPERTY_USD} ={" "}
              <span className="text-[#111111]">{flatPriceLabel}</span>/month
            </p>
          ) : null}
          <p className="mt-1 text-[11px] text-[#5F6368]">{displayedHelper}</p>

          <div className="mt-3 pt-3 border-t border-[#EAEAEA] flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-[11px] text-[#5F6368]">
              <TrendingUp size={12} className="text-[#10B981]" />
              Early/late check-in/out can more than cover the monthly cost
            </div>
            <button
              onClick={() => onSubscribe("stage_pricing")}
              disabled={actionLoading || enabledCount === 0}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-[12px] text-[13px] font-bold transition-colors ${
                enabledCount === 0
                  ? "bg-[#EAEAEA] text-[#888888] cursor-not-allowed"
                  : "bg-[#EF4949] text-white hover:bg-[#DC2626] shadow-[0_8px_32px_rgba(239,73,73,0.12)]"
              }`}
            >
              {actionLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : enabledCount === 0 ? (
                "Select at least one service"
              ) : (
                <>
                  {pricing.ctaLabel}
                  <ArrowRight size={13} strokeWidth={2.5} />
                </>
              )}
            </button>
          </div>

          <div className="mt-2.5 flex items-center gap-3 text-[10px] text-[#888888]">
            <span className="flex items-center gap-1"><Shield size={10} /> {isQuote ? "Custom onboarding" : "Cancel anytime"}</span>
            <span className="flex items-center gap-1"><CreditCard size={10} /> {isQuote ? "Team follow-up by email" : "Secure checkout"}</span>
            <span className="hidden laptop:flex items-center gap-1"><Sparkles size={10} className="text-[#EF4949]" /> Guest app + unlimited requests included</span>
          </div>
        </div>
      </div>

      {/* Right — phone */}
      <div className="relative order-1 laptop:order-2 overflow-hidden laptop:min-h-0 h-[360px] tabletOnly:h-[440px] laptop:h-auto">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(500px 400px at 55% 45%, rgba(17,17,17,0.05), transparent 60%)",
          }}
        />
        <div
          className={`relative h-full flex items-center justify-center ${
            compactHeight ? "py-1 laptop:py-4" : "py-2 laptop:py-6"
          }`}
        >
          <PhoneAnchor
            stage={3}
            enabledServices={enabledServices}
            hostLogo={hostLogo}
            compactHeight={compactHeight}
          />
        </div>
      </div>
    </div>
  );
}

function Calc({ label, value, icon }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">
        {icon}
        {label}
      </div>
      <div className="text-[18px] laptop:text-[22px] font-extrabold text-[#111111] leading-tight tabular-nums mt-0.5" style={{ letterSpacing: -0.3 }}>
        {value}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="h-8 w-px bg-[#EAEAEA]" />;
}
